import { OrderedMap } from 'immutable';
import type { ID, ReduxAction } from '@repo/utils/types';
import Occasion from '@repo/utils/helpers/Occasion';
import AppState from '../app-state';
import { contactNotesSelector } from '../../actions/notes';
import {
  ContactTimeline,
  TimelineFilter,
  TimelineItem,
  TimelineWindowSummary,
  FilteredTimelineCollection,
  FilteredTimelineCollectionsMap,
} from '@repo/utils/models/Timeline';

type ContactID = ID;

const allFilterValues: TimelineFilter[] = ['notes', 'reminders', 'emails', 'events', 'calls'];

export const selectTimeline = (contactID: ID) => (state: AppState) =>
  state.data.timelines.collection.get(String(contactID), new ContactTimeline());

// TODO: when a new note is persisted, add it to the timeline in the reducer? may not want to do that if it causes complications with the new note flow
export const selectContactTimeline =
  (contactID: ID, condensed: boolean = false) =>
  (state: AppState): ContactTimeline => {
    const contactNotes = contactNotesSelector(contactID)(state);
    const timeline = selectTimeline(contactID)(state);
    const user = state.user;

    if (contactNotes.length !== timeline.noteIDs.size) {
      let pastItems = timeline.pastItems;

      if (condensed || !timeline.windowLoaded) {
        pastItems = timeline.condensedPastItems;
      }

      // notes to add should be those that have been persisted since the timeline was fetched
      const firstPastItemDate = new Occasion(pastItems.first()?.date);
      const notesToAdd = OrderedMap(
        contactNotes
          .filter((note) => firstPastItemDate.isBefore(note.created))
          .sort((a, b) => (a.created.isBefore(b.created) ? 1 : -1))
          .map((note) => [
            note.id,
            new TimelineItem({
              id: note.id,
              item_type: 'note',
              noteID: note.id,
              users: [{ ...user, is_self: true }],
              metadata: { reminder: note.reminder },
              date: note.created,
            }),
          ]),
      );

      const timelineWithNewNotes = timeline.upcomingItems.concat(notesToAdd).concat(pastItems);
      return timeline.copyWith({ items: timelineWithNewNotes });
    }

    if (condensed) {
      return timeline.copyWith({
        items: timeline.items.filter((item) => timeline.condensedViewIDs.has(item.id)),
      });
    }

    return timeline;
  };

export const selectContactTimelineWindowSummary =
  (contactID: ID) =>
  (state: AppState): TimelineWindowSummary | undefined =>
    state.data.timelines.windowSummaries.get(String(contactID));

export const selectFilteredContactTimeline =
  (contactID: ContactID, filter: TimelineFilter) =>
  (state: AppState): FilteredTimelineCollection | undefined =>
    state.data.timelines.filteredCollections.get(contactID)?.[filter];

export const selectContactTimelineAvailableFilters =
  (contactID: ContactID) =>
  (state: AppState): TimelineFilter[] =>
    state.data.timelines.availableFilters.get(contactID) || [];

export interface TimelinesState {
  collection: OrderedMap<ContactID, ContactTimeline>;
  windowSummaries: OrderedMap<ContactID, TimelineWindowSummary>;
  filteredCollections: OrderedMap<ContactID, FilteredTimelineCollectionsMap>;
  availableFilters: OrderedMap<ContactID, TimelineFilter[]>;
}

const initialTimelinesState: TimelinesState = {
  collection: OrderedMap<ContactID, ContactTimeline>(),
  windowSummaries: OrderedMap<ContactID, TimelineWindowSummary>(),
  filteredCollections: OrderedMap<ContactID, FilteredTimelineCollectionsMap>(),
  availableFilters: OrderedMap<ContactID, TimelineFilter[]>(),
};

export function timelineReducer(
  state: TimelinesState = initialTimelinesState,
  { type, payload }: ReduxAction,
) {
  const contactID = String(payload?.contact?.id || payload?.contactID);

  switch (type) {
    case 'FETCH_CONTACT_TIMELINE_REQUESTED':
      return {
        ...state,
        collection: state.collection.update(
          contactID,
          new ContactTimeline({ isFetching: true }),
          (tl) => tl.copyWith({ isFetching: true, error: false }),
        ),
      };

    case 'FETCH_CONTACT_TIMELINE_WINDOW_REQUESTED':
      return {
        ...state,
        collection: state.collection.update(contactID, (tl) => tl?.copyWith({ isFetching: true })),
      };

    case 'FETCH_CONTACT_TIMELINE_SUCCEEDED':
    case 'FETCH_CONTACT_TIMELINE_WINDOW_SUCCEEDED':
      if (payload.window) {
        return {
          ...state,
          collection: state.collection.update(contactID, (tl) => {
            const results = payload.response.results || [];
            const mergedItems = tl.items.merge(
              OrderedMap(results.map((item) => [item.id, new TimelineItem(item)])),
            );

            const sortedItems = mergedItems.sort((a, b) => {
              const dateA = new Occasion(a.date);
              const dateB = new Occasion(b.date);
              return dateA.isBefore(dateB) ? 1 : -1;
            });

            return tl?.copyWith({
              items: sortedItems,
              showBtnAfterID: results[results.length - 1]?.id,
              condensedView: false,
              windowLoaded: true,
              isFetching: false,
            });
          }),
        };
      }

      return {
        ...state,
        collection: state.collection.set(
          contactID,
          new ContactTimeline({
            items: (payload?.response?.results || []).map((item) => [
              item.id,
              new TimelineItem(item),
            ]),
            showBtnAfterID: payload.response?.pagination_item_id,
            condensedView: true,
            isFetching: false,
            updated: new Occasion(),
          }),
        ),
        ...(payload?.dropExistingWindowSummary
          ? { windowSummaries: state.windowSummaries.delete(contactID) }
          : {}),
      };

    case 'FETCH_CONTACT_TIMELINE_WINDOW_SUMMARY_REQUESTED':
      return {
        ...state,
        windowSummaries: state.windowSummaries.update(contactID, (ws) =>
          ws?.copyWith({ isFetching: true }),
        ),
      };

    case 'FETCH_CONTACT_TIMELINE_WINDOW_SUMMARY_SUCCEEDED':
      const tl = state.collection.get(contactID);
      const windowSummary = state.windowSummaries.get(contactID);

      const itemsInWindow = tl?.itemsInWindow(
        new Occasion(payload.start),
        new Occasion(payload.end),
      );

      const nextWindow = new TimelineWindowSummary({
        ...payload.response,
        // We use next_date as the start date for the next window. If we have yet to load a window summary,
        // we use the start date of the first item in the timeline (this is determined in the epic)
        ...(!windowSummary ? { next_date: payload.start } : {}),
        adjustments: itemsInWindow,
        isFetching: false,
      });

      return {
        ...state,
        windowSummaries: state.windowSummaries.set(contactID, nextWindow),
      };

    case 'FETCH_FILTERED_CONTACT_TIMELINE_REQUESTED':
      return {
        ...state,
        filteredCollections: state.filteredCollections.update(contactID, (tls) => {
          const filteredTl = tls?.[payload.filterType] || new FilteredTimelineCollection();
          return {
            ...tls,
            [payload.filterType]: filteredTl.copyWith({ isFetching: true }),
          };
        }),
      };

    case 'FETCH_FILTERED_CONTACT_TIMELINE_SUCCEEDED':
      return {
        ...state,
        filteredCollections: state.filteredCollections.update(contactID, (tls) => {
          const filteredTl = tls?.[payload.filterType] || new FilteredTimelineCollection();
          const results = payload.response.results || [];
          const mergedItems = filteredTl.items.merge(
            OrderedMap(results.map((item) => [item.id, new TimelineItem(item)])),
          );

          return {
            ...tls,
            [payload.filterType]: filteredTl.copyWith({
              items: mergedItems,
              has_next: payload.response.has_next,
              lastDate: payload.response.last_date,
              isFetching: false,
            }),
          };
        }),
      };

    case 'FETCH_CONTACT_TIMELINE_AVAILABLE_FILTERS_SUCCEEDED':
      const availableFilters = allFilterValues.filter((f) => !!payload?.response?.[f]);
      return {
        ...state,
        availableFilters: state.availableFilters.set(contactID, availableFilters),
      };

    case 'DELETE_NOTE':
      let contactTimeline = state.collection.get(contactID);

      if (!contactTimeline) {
        return state;
      }

      let filteredCollectionsMap = state.filteredCollections.get(contactID);

      if (!!filteredCollectionsMap?.notes || !!filteredCollectionsMap?.reminders) {
        filteredCollectionsMap = {
          ...filteredCollectionsMap,
          notes: filteredCollectionsMap.notes?.copyWith({
            items: filteredCollectionsMap.notes.items.filter(
              (item) => item.noteID !== String(payload.id),
            ),
          }),
          reminders: filteredCollectionsMap.reminders?.copyWith({
            items: filteredCollectionsMap.reminders.items.filter(
              (item) => item.noteID !== String(payload.id),
            ),
          }),
        };
      }

      const timelineIDBeingRemoved = contactTimeline.items.find(
        (item) => item.noteID === String(payload.id),
      )?.id;
      if (timelineIDBeingRemoved && contactTimeline.showBtnAfterID === timelineIDBeingRemoved) {
        const idxOfRemovedItem = contactTimeline.items
          .keySeq()
          .findIndex((id) => id === timelineIDBeingRemoved);
        const itemsArray = contactTimeline.items.toArray();
        if (idxOfRemovedItem > 0) {
          contactTimeline = contactTimeline.copyWith({
            showBtnAfterID: itemsArray[idxOfRemovedItem - 1].id,
          });
          // I think this is quite an edge case, but it is a hail mary to keep the button in the UI no matter what
        } else if (idxOfRemovedItem === 0 && itemsArray.length > 1) {
          contactTimeline = contactTimeline.copyWith({
            showBtnAfterID: itemsArray[1].id,
          });
        }
      }

      return {
        ...state,
        collection: state.collection.set(
          contactID,
          contactTimeline.copyWith({
            items: contactTimeline.items.filter((item) => item.noteID !== String(payload.id)),
          }),
        ),
        filteredCollections: state.filteredCollections.set(contactID, filteredCollectionsMap),
      };

    case 'FETCH_CONTACT_TIMELINE_FAILED':
      // case 'FETCH_CONTACT_TIMELINE_WINDOW_FAILED':
      return {
        ...state,
        collection: state.collection.update(contactID, (tl) =>
          tl?.copyWith({ isFetching: false, error: true }),
        ),
      };

    default:
      return state;
  }
}
